import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherCheckCircle,
  FeatherFlame,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { format } from "date-fns";
import { toast } from "sonner";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { useGetHotHoldingAlertQueryControllerGet } from "@/generated/hot-holding-alerts/hot-holding-alerts";
import { HotHoldingAlertDetailResponseDtoReadingType } from "@/generated/model/hotHoldingAlertDetailResponseDtoReadingType";
import type { RecordedByDto } from "@/generated/model/recordedByDto";
import { useOfflineHotHoldingAlertAck } from "@/hooks/use-offline-hot-holding-alert-ack";
import { FEATURES } from "@/lib/features";
import {
  ALERT_ACK_ERROR,
  ALERT_ACK_QUEUED,
  ALERT_ACKNOWLEDGED,
} from "@/lib/monitoring/acknowledge-copy";
import { monitoringAlertPill } from "@/lib/workspace/status-pill";

export const Route = createFileRoute(
  "/_authenticated/hot-holding-alerts/$alertId",
)({
  component: HotHoldingAlertRoute,
});

function HotHoldingAlertRoute() {
  return (
    <FeatureGate feature={FEATURES.MONITORING} featureName="Monitoring">
      <HotHoldingAlertDetailPage />
    </FeatureGate>
  );
}

function personName(user: RecordedByDto | null): string {
  if (!user) return "someone";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email;
}

function HotHoldingAlertDetailPage() {
  const { alertId } = useParams({
    from: "/_authenticated/hot-holding-alerts/$alertId",
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } =
    useGetHotHoldingAlertQueryControllerGet(alertId);
  const alert = data?.data;

  const { acknowledge, isPending, syncStatus } =
    useOfflineHotHoldingAlertAck(alertId);

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({ to: "/dashboard" });
    }
  };

  // The offline ack hook only queues the change, so this both refreshes the
  // surfaces that render the alert and confirms the server actually took it —
  // the queue swallows HTTP failures, so a bare success toast would lie.
  const handleAcknowledge = async () => {
    try {
      await acknowledge();
    } catch {
      toast.error(ALERT_ACK_ERROR);
      return;
    }

    // Offline the ack sits in the sync queue and React Query pauses refetches,
    // so there is nothing to confirm against yet.
    if (!syncStatus.isOnline) {
      toast.success(ALERT_ACK_QUEUED);
      return;
    }

    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        if (typeof key !== "string") return false;
        if (key.includes("/hot-holding-alerts")) return true;
        if (key.includes("/monitoring")) return true;
        return key.includes("/dashboard");
      },
      refetchType: "all",
    });

    const refreshed = await refetch();
    if (!refreshed.data?.data.acknowledgedAt) {
      toast.error(ALERT_ACK_ERROR);
      return;
    }
    toast.success(ALERT_ACKNOWLEDGED);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (error || !alert) {
    return (
      <div className="flex w-full flex-col items-start gap-4">
        <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 bg-white p-6">
          <span className="text-body font-body text-default-font">
            Alert not found or you don&apos;t have access.
          </span>
        </div>
      </div>
    );
  }

  const isAcknowledged = alert.acknowledgedAt !== null;

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1 pb-6">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <div className="flex w-full items-center gap-2">
              <IconButton icon={<FeatherArrowLeft />} onClick={handleBack} />
              <FeatherFlame className="h-5 w-5 text-warning-800" />
              <span className="grow shrink-0 basis-0 whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
                {alert.mealName}
              </span>
              <WorkspaceStatusBadge
                pill={monitoringAlertPill(
                  isAcknowledged ? "RESOLVED" : "ACTIVE",
                )}
              />
            </div>
            <span className="text-body font-body text-subtext-color">
              Recorded {format(new Date(alert.recordedAt), "PPp")}
              {alert.acknowledgedAt && (
                <>
                  {" · acknowledged "}
                  {format(new Date(alert.acknowledgedAt), "PPp")}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex w-full max-w-3xl mx-auto flex-col items-start gap-6 pb-8">
        {!isAcknowledged && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <Button
              className="h-10"
              variant="brand-primary"
              size="medium"
              icon={<FeatherCheckCircle />}
              onClick={handleAcknowledge}
              disabled={isPending}
            >
              {isPending ? "Acknowledging…" : "Acknowledge alert"}
            </Button>
          </div>
        )}

        {/* Triggering reading callout */}
        <section className="flex w-full flex-col items-start gap-3 rounded-md border border-warning-200 bg-warning-50 p-4">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Triggering reading
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              {alert.temperature}°C
            </span>
            <Badge variant="warning">Out of range</Badge>
            <span className="text-caption font-caption text-subtext-color">
              policy floor {alert.thresholdAtLog}°C
            </span>
            <Badge variant="neutral">
              {alert.readingType ===
              HotHoldingAlertDetailResponseDtoReadingType.DELIVERY
                ? "Delivery"
                : "Service"}
            </Badge>
          </div>
          {alert.notes && (
            <span className="text-caption font-caption text-default-font">
              “{alert.notes}”
            </span>
          )}
          <span className="text-caption font-caption text-subtext-color">
            Recorded {format(new Date(alert.recordedAt), "PPp")}
            {alert.recordedBy && ` by ${personName(alert.recordedBy)}`}
          </span>
        </section>

        {/* Acknowledgement panel (only once acknowledged) */}
        {isAcknowledged && (
          <section className="flex w-full flex-col items-start gap-3 rounded-md border border-success-200 bg-success-50 p-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Acknowledgement
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="success">Acknowledged</Badge>
              <span className="text-caption font-caption text-subtext-color">
                {alert.acknowledgedAt &&
                  format(new Date(alert.acknowledgedAt), "PPp")}
              </span>
            </div>
            <span className="text-body font-body text-default-font">
              Acknowledged by {personName(alert.acknowledgedBy)}
            </span>
          </section>
        )}
      </div>
    </div>
  );
}
